import type { ChatMessage } from "@/components/ai-chat/types";

import type { ChatHistoryEntry, ChatThreadSummary } from "@/types/chat-thread";

/** Query parameter that tells an editor page to reopen one assistant thread. */
export const ASSISTANT_THREAD_PARAM = "assistantThreadId";

const PAGE_ASSISTANTS: Record<
  string,
  { label: string; path: (entityId: string) => string }
> = {
  TEMPLATE_ASSISTANT: {
    label: "Template Assistant",
    path: (id) => `/settings/note-templates/${id}`,
  },
  FORM_ASSISTANT: {
    label: "Form Assistant",
    path: (id) => `/settings/forms/edit/${id}`,
  },
  OUTREACH_ASSISTANT: {
    label: "Sequence Assistant",
    path: (id) => `/comms/sequences/${id}`,
  },
  LANDING_PAGE_ASST: {
    label: "Landing Page",
    path: (id) => `/settings/ai-agents/reports/${id}`,
  },
};

export const PAGE_ASSISTANT_CHAT_TYPES = Object.keys(PAGE_ASSISTANTS);

export function pageAssistantLabel(chatType?: string): string | undefined {
  return chatType ? PAGE_ASSISTANTS[chatType]?.label : undefined;
}

/** Where to reopen a page assistant thread, or null when it opens in Ask Decoda. */
export function pageAssistantThreadHref(
  thread: Pick<ChatThreadSummary, "threadId" | "chatType" | "entityId">,
  tenantSubDomain: string,
): string | null {
  const assistant = thread.chatType
    ? PAGE_ASSISTANTS[thread.chatType]
    : undefined;
  if (!assistant || !thread.entityId) return null;
  const query = new URLSearchParams({
    [ASSISTANT_THREAD_PARAM]: thread.threadId,
  });
  return `/${tenantSubDomain}${assistant.path(thread.entityId)}?${query}`;
}

/** Page assistant turns as editor chat messages; Ask Decoda has its own mapping. */
export function pageAssistantHistoryToMessages(
  entries: ChatHistoryEntry[],
): ChatMessage[] {
  return entries.flatMap((entry) => {
    const timestamp = new Date(entry.createdDate);
    const question: ChatMessage = {
      id: `${entry.id}:question`,
      role: "user",
      content: entry.question,
      timestamp,
    };
    if (!entry.answer) return [question];
    return [
      question,
      { id: entry.id, role: "assistant", content: entry.answer, timestamp },
    ];
  });
}
